import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, ShieldCheck, CheckCircle2, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { useAuth } from "@/contexts/AuthContext";
import BillingCenterTab from "@/components/BillingCenterTab";
import AvgsPayments from "@/components/AvgsPayments";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TABS = [["avgs", "Rechnungen"], ["zahlungen", "Zahlungen AVGS"], ["abschlaege", "Abschläge Weiterbildung"], ["wb", "WB-Verträge"], ["datev", "DATEV-Protokoll"]];
const eur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

function WbOverview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/billing/wb-overview").then((r) => setD(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message)); }, []);
  if (!d) return <div className="text-xs text-slate-400">Lädt…</div>;
  return (
    <div data-testid="billing-wb">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Stat label="Vertragsvolumen (unterschrieben)" value={eur(d.total)} testId="billing-wb-total" />
        <Stat label="Teilnehmer" value={d.rows.length} testId="billing-wb-count" />
        <Stat label="Noch nicht an DATEV" value={d.open_datev} testId="billing-wb-open" warn={d.open_datev > 0} />
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead><tr className="bg-slate-50 text-[10px] uppercase tracking-overline text-slate-500 text-left">
            <th className="px-4 py-3">Teilnehmer</th><th className="px-3">Zeitraum</th><th className="px-3">Vertrag</th><th className="px-3">DATEV</th>
            <th className="px-3 text-right">Gesamt</th><th className="px-3">Nächster Abschlag</th><th className="px-3 text-right">Bisher fällig</th></tr></thead>
          <tbody>{d.rows.map((r) => (
            <tr key={r.participant_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`billing-wb-row-${r.participant_id}`}>
              <td className="px-4 py-2.5"><Link to={`/weiterbildung/kunden/${r.participant_id}?tab=unterlagen`} className="hover:underline">{r.name}</Link></td>
              <td className="px-3 text-xs font-mono">{r.start ? `${formatDeShort(r.start)}–${formatDeShort(r.end)}` : "—"}</td>
              <td className="px-3 text-xs">{r.signed_at ? `unterschrieben ${formatDeShort(r.signed_at)}` : <span className="text-slate-400">offen</span>}</td>
              <td className="px-3 text-xs">{r.datev_status === "sent" ? <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 size={12} /> {formatDeShort(r.datev_sent_at)}</span>
                : r.signed_at ? <span className="text-red-600 flex items-center gap-1"><AlertCircle size={12} /> fehlt</span> : "—"}</td>
              <td className="px-3 text-right font-mono">{eur(r.total)}</td>
              <td className="px-3 text-xs">{r.next_installment ? `${r.next_installment.nr}/${r.installments} · ${formatDeShort(r.next_installment.date)} · ${eur(r.next_installment.amount)}` : "—"}</td>
              <td className="px-3 text-right font-mono text-xs">{eur(r.paid_out)}</td>
            </tr>))}
          </tbody>
        </table>
        {d.rows.length === 0 && <div className="p-8 text-center text-sm text-slate-500">Keine Weiterbildungsteilnehmer.</div>}
      </div>
    </div>
  );
}

function PaidDialog({ row, onClose, onDone }) {
  const [f, setF] = useState({ paid_on: new Date().toISOString().slice(0, 10), amount: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (row) setF({ paid_on: new Date().toISOString().slice(0, 10), amount: String(row.amount).replace(".", ",") }); }, [row]);
  const book = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/billing/wb-installments/${row.participant_id}/${row.nr}/paid`, { paid_on: f.paid_on, amount: Number(String(f.amount).replace(",", ".")) });
      toast.success(`Gebucht · Rechnung ${data.invoice_number} an DATEV übertragen`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="wb-paid-dialog">
        <DialogHeader><DialogTitle>Abschlag als bezahlt buchen</DialogTitle></DialogHeader>
        {row && <p className="text-xs text-slate-500">{row.name} · {row.nr}. von {row.count} Abschlägen · Leistungsträger: {row.kostentraeger || "— fehlt —"}</p>}
        <label className="text-xs text-slate-600 flex flex-col">Zahlungseingang am<input type="date" value={f.paid_on} onChange={(e) => setF({ ...f, paid_on: e.target.value })} className="input-tech mt-1" data-testid="wb-paid-date" /></label>
        <label className="text-xs text-slate-600 flex flex-col">Gezahlter Betrag (€)<input value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="input-tech mt-1" data-testid="wb-paid-amount" /></label>
        <p className="text-[11px] text-slate-500">Es wird automatisch eine Rechnung auf den Leistungsträger mit dem gezahlten Betrag erstellt und nur an DATEV (Ausgangsrechnungen) übertragen. Der Leistungsträger erhält keine Rechnung.</p>
        <button onClick={book} disabled={busy} className="w-full bg-slate-900 text-white py-3 text-sm font-medium hover:bg-black disabled:opacity-50" data-testid="wb-paid-confirm">{busy ? "Bucht…" : "Bezahlt buchen & an DATEV"}</button>
      </DialogContent>
    </Dialog>
  );
}

function Installments() {
  const [d, setD] = useState(null);
  const [row, setRow] = useState(null);
  const [filter, setFilter] = useState("open");
  const load = () => api.get("/billing/wb-installments").then((r) => setD(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-xs text-slate-400">Lädt…</div>;
  const today = new Date().toISOString().slice(0, 10);
  const rows = d.rows.filter((r) => filter === "all" || (filter === "open" ? !r.payment : !!r.payment));
  return (
    <div data-testid="billing-installments">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <Stat label="Fällig & offen" value={eur(d.open_due)} testId="inst-open-due" warn={d.open_due > 0} />
        <Stat label="Bereits eingegangen" value={eur(d.paid)} testId="inst-paid" />
      </div>
      <div className="flex gap-1.5 mb-4" data-testid="inst-filter">
        {[["open", "Offen"], ["paid", "Bezahlt"], ["all", "Alle"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`inst-filter-${k}`} className={`px-3 py-1.5 text-xs border ${filter === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>{l}</button>
        ))}
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[1180px]">
          <thead><tr className="bg-slate-50 text-[10px] uppercase tracking-overline text-slate-500 text-left">
            <th className="px-4 py-3">Fällig am</th><th className="px-3">Teilnehmer</th><th className="px-3">Kundennr.</th><th className="px-3">Maßnahmenr.</th><th className="px-3">Zeitraum WB</th><th className="px-3">Leistungsträger</th><th className="px-3">Abschlag</th><th className="px-3 text-right">Betrag</th><th className="px-3">Status</th><th className="px-3"></th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={`${r.participant_id}-${r.nr}`} className={`border-t border-slate-100 ${!r.payment && r.date < today ? "bg-red-50 text-red-700" : ""}`} data-testid={`inst-row-${r.participant_id}-${r.nr}`}>
              <td className="px-4 py-2.5 font-mono text-xs">{formatDeShort(r.date)}</td>
              <td className="px-3"><Link to={`/weiterbildung/kunden/${r.participant_id}?tab=unterlagen`} className="hover:underline">{r.name}</Link></td>
              <td className="px-3 text-xs font-mono" data-testid={`inst-custno-${r.participant_id}-${r.nr}`}>{r.customer_number || "—"}</td>
              <td className="px-3 text-xs font-mono" data-testid={`inst-measno-${r.participant_id}-${r.nr}`}>{r.measure_number || "—"}</td>
              <td className="px-3 text-xs font-mono whitespace-nowrap">{formatDeShort(r.start)}–{formatDeShort(r.end)}</td>
              <td className="px-3 text-xs">{r.kostentraeger || <span className="text-red-600">fehlt</span>}</td>
              <td className="px-3 text-xs">{r.nr} / {r.count}</td>
              <td className="px-3 text-right font-mono">{eur(r.payment ? r.payment.amount : r.amount)}</td>
              <td className="px-3 text-xs">{r.payment ? <span className="text-emerald-700">bezahlt {formatDeShort(r.payment.paid_on)} · {r.payment.invoice_number}{r.payment.datev_status !== "sent" ? " · DATEV fehlgeschlagen" : ""}</span> : r.date < today ? <span className="text-red-600">überfällig</span> : "offen"}</td>
              <td className="px-3 text-right">{!r.payment && <button onClick={() => setRow(r)} className="px-3 py-1.5 text-xs bg-slate-900 text-white hover:bg-black" data-testid={`inst-pay-${r.participant_id}-${r.nr}`}>Bezahlt</button>}</td>
            </tr>))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="p-8 text-center text-sm text-slate-500" data-testid="inst-empty">Keine Abschläge in dieser Ansicht.</div>}
      </div>
      <PaidDialog row={row} onClose={() => setRow(null)} onDone={() => { setRow(null); load(); }} />
    </div>
  );
}

function DatevLog() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/billing/datev-log").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <div className="text-xs text-slate-400">Lädt…</div>;
  return (
    <div className="border border-slate-200 bg-white divide-y divide-slate-100" data-testid="billing-datev-log">
      {rows.length === 0 && <div className="p-8 text-center text-sm text-slate-500">Noch keine Belege an DATEV übertragen.</div>}
      {rows.map((r, i) => (
        <div key={i} className="px-5 py-3 flex items-center gap-3 text-sm">
          <span className={`px-2 py-0.5 text-[10px] ${r.category === "invoice_accounting" ? "bg-slate-100 text-slate-700" : "bg-[#0a788c]/10 text-[#0a788c]"}`}>{r.category === "wb_installment_accounting" ? "WB-Abschlag" : r.category === "wb_contract_accounting" ? "WB-Vertrag" : "AVGS-Rechnung"}</span>
          <span className="flex-1 min-w-0 truncate">{r.subject}</span>
          <span className={`text-[11px] ${r.status === "sent" ? "text-emerald-700" : "text-red-600"}`}>{r.status === "sent" ? "übertragen" : r.status}</span>
          <span className="text-[11px] font-mono text-slate-400">{formatDeShort(r.sent_at)}</span>
        </div>
      ))}
    </div>
  );
}

const Stat = ({ label, value, testId, warn }) => (
  <div className="border border-slate-200 bg-white p-4" data-testid={testId}>
    <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">{label}</div>
    <div className={`font-heading text-2xl font-light tracking-tighter ${warn ? "text-red-600" : "text-slate-900"}`}>{value}</div>
  </div>
);

function AccessDialog({ open, onClose }) {
  const [users, setUsers] = useState([]);
  useEffect(() => { if (open) api.get("/billing/access").then((r) => setUsers(r.data)); }, [open]);
  const toggle = async (u, perm) => {
    const cur = u.billing_perms || [];
    let next = cur.includes(perm) ? cur.filter((p) => p !== perm) : [...cur, perm];
    if (perm === "view" && cur.includes("view")) next = [];
    try {
      const { data } = await api.put(`/billing/access/${u.user_id}`, { perms: next });
      setUsers((xs) => xs.map((x) => (x.user_id === u.user_id ? { ...x, billing_perms: data.billing_perms } : x)));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white max-h-[85vh] overflow-y-auto" data-testid="billing-access-dialog">
        <DialogHeader><DialogTitle>Zugriff Abrechnung</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Admins haben immer vollen Zugriff. Pro Person festlegen: <b>Sehen</b> (Rechnungen, Beträge, DATEV-Protokoll), <b>Senden</b> (Rechnungen erstellen und versenden, Korrekturen, DATEV-Übertragung) und <b>Löschen</b>. Die Übertragung an DATEV passiert beim Versand automatisch.</p>
        <div className="border border-slate-200">
          <div className="grid grid-cols-[1fr_56px_56px_56px] px-4 py-2 bg-slate-50 text-[10px] font-bold uppercase tracking-overline text-slate-500"><span>Person</span><span className="text-center">Sehen</span><span className="text-center">Senden</span><span className="text-center">Löschen</span></div>
          {users.filter((u) => u.role !== "admin").map((u) => (
            <div key={u.user_id} className="grid grid-cols-[1fr_56px_56px_56px] items-center px-4 py-2.5 text-sm border-t border-slate-100" data-testid={`billing-access-${u.user_id}`}>
              <span className="min-w-0">{u.name}<span className="block text-[11px] text-slate-400 truncate">{u.email}</span></span>
              {["view", "send", "delete"].map((perm) => (
                <span key={perm} className="text-center"><input type="checkbox" checked={(u.billing_perms || []).includes(perm)} onChange={() => toggle(u, perm)} data-testid={`billing-perm-${perm}-${u.user_id}`} /></span>
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AbrechnungPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_master_admin || (user?.all_roles || [user?.role]).includes("admin");
  const allowed = isAdmin || (user?.billing_perms || []).includes("view");
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "avgs";
  const [access, setAccess] = useState(false);
  if (!allowed) return (
    <div className="max-w-xl mx-auto p-10 text-center" data-testid="billing-no-access">
      <div className="font-heading text-2xl font-light tracking-tighter text-slate-900 mb-2">Kein Zugriff</div>
      <p className="text-sm text-slate-500">Der Abrechnungsbereich ist nur für eingeladene Personen verfügbar.</p>
    </div>
  );
  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-8" data-testid="abrechnung-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 flex items-center gap-1.5"><Wallet size={12} /> Geschützter Bereich</div>
          <h1 className="font-heading text-3xl font-light tracking-tighter text-slate-900">Abrechnung</h1>
          <p className="text-sm text-slate-500 mt-1">Rechnungen (AVGS + WB, fortlaufender Nummernkreis), Abschläge der Weiterbildung und DATEV-Übertragungen.</p>
        </div>
        {isAdmin && <button onClick={() => setAccess(true)} className="px-4 py-2.5 text-xs font-medium border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid="billing-access-btn"><ShieldCheck size={12} /> Zugriff verwalten</button>}
      </div>
      <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto" data-testid="billing-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setParams({ tab: k })} data-testid={`billing-tab-${k}`}
            className={`px-4 py-2 text-sm -mb-px border-b-2 whitespace-nowrap ${tab === k ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>
      {tab === "avgs" && <BillingCenterTab />}
      {tab === "zahlungen" && <AvgsPayments />}
      {tab === "abschlaege" && <Installments />}
      {tab === "wb" && <WbOverview />}
      {tab === "datev" && <DatevLog />}
      <AccessDialog open={access} onClose={() => setAccess(false)} />
    </div>
  );
}
